import { useState } from "react";
import { useServerSdk } from "../../RootNavigator/services/ServerSdk";
import { OrganizationImpactSectionDescriptor } from "./utilities";
import Divider from "@hpo/client/components/Divider";
import { OrganizationImpactSectionParams } from "@hpo/client/models/Archive";
import FieldsForm from "@hpo/client/utilities/fields/FieldsForm";
import TextField from "@hpo/client/utilities/fields/TextField";
import { useSubmitCallback } from "@hpo/client/utilities/useSubmitCallback";
import MultiSelectField from "@hpo/client/utilities/fields/MultiSelectField";
import Spacer from "@hpo/client/utilities/Spacer";
import Buttons from "@hpo/client/components/Buttons";
import Button from "@hpo/client/components/Button";
import { find } from "@hpo/client/utilities/is";
import { Tag } from "@hpo/client/models/Tag";
import ErrorToast from "@hpo/client/utilities/ErrorToast";
import T from "@hpo/client/components/text/Text";
import CheckboxField from "@hpo/client/utilities/fields/CheckboxField";
import EmptyView from "@hpo/client/utilities/EmptyView";
import DropDownField from "@hpo/client/utilities/fields/DropDownField";

type OrganizationImpactParamsProps = {
  params: Partial<OrganizationImpactSectionParams>;
  onChange: (params: OrganizationImpactSectionParams) => void;
  onDelete: () => void;
};

export default function OrganizationImpactParams(
  props: OrganizationImpactParamsProps,
) {
  const { params, onDelete, onChange } = props;
  const server = useServerSdk();
  const allPeriods = server.suspender.use(server.getPeriods_());
  const allOrganizations = server.suspender.use(server.getOrganizations());
  const tags = server.suspender.use(server.getTags());
  const [title, setTitle] = useState<string | null>(params.title || null);
  const [periods, setPeriods] = useState<Array<string>>(params.periods || []);
  const [orgs, setOrgs] = useState<Array<string>>(params.orgs || []);

  const [tag, setTag] = useState<Tag | null>(
    params.tag ? find(tags, (t) => t.id === params.tag) : null,
  );

  const [onSubmit, submission] = useSubmitCallback(() => {
    if (title === null) throw new Error("Titre manquant");
    if (periods.length === 0) throw new Error("Périodes manquantes");
    if (tag === null) throw new Error("Etiquette manquantes");
    if (orgs.length === 0)
      throw new Error("Sélectionnez au moins un bénéficiaire");
    onChange({
      type: "organization-impact",
      title,
      periods,
      tag: tag.id,
      orgs,
    });
  }, [title, periods, tag, orgs]);

  return (
    <FieldsForm onSubmit={onSubmit}>
      <ErrorToast error={submission.error} />
      <T style="section">{OrganizationImpactSectionDescriptor.title}</T>
      <Spacer />
      <T>{OrganizationImpactSectionDescriptor.description}</T>
      <Spacer />
      <Divider />
      <Spacer />
      <TextField
        value={title}
        label="Titre de la section"
        onChange={setTitle}
      />
      <Spacer />
      <MultiSelectField
        value={periods}
        label="Périodes à afficher"
        onChange={setPeriods}
        options={allPeriods}
        keyExtractor={(o) => o}
        renderOption={(o) => ({ label: o })}
      />
      <Spacer />
      <CheckboxField
        value={orgs}
        label="Bénéficiaires"
        onChange={(p) => setOrgs(p)}
        options={allOrganizations}
        keyExtractor={(o) => o.id}
        renderOption={(o) => ({ label: o.businessName })}
        help="Seuls les indicateurs des projets sélectionnés seront pris en compte"
        renderEmpty={() => (
          <EmptyView
            message="Sélectionnez un appel à projet"
            style="discreet"
          />
        )}
      />
      <Spacer />
      <DropDownField
        value={tag}
        label="Etiquette"
        onChange={setTag}
        options={tags}
        keyExtractor={(t) => t.id}
        renderOption={(t) => ({ label: t.label })}
        help="Seuls les indicateurs rattachés aux étiquettes sélectionnées seront pris en compte."
      />
      <Spacer />
      <Buttons expand>
        <Button onClick={onDelete} label="Supprimer" style="danger" />
        <Button submit label="Valider" />
      </Buttons>
    </FieldsForm>
  );
}
